import type {
  McpAuthMode,
  McpInspectResult,
} from '@/features/Integrations/types/integration';

/** Header padrão do modo `apikey`, quando o servidor não declara outro. */
export const DEFAULT_API_KEY_HEADER = 'X-API-Key';

/** A api só aceita nome de header com letras, números e `-` (`authHeader` do validator). */
const HEADER_NAME_RE = /^[A-Za-z0-9-]{1,100}$/;

/** Nome de header digitado, ou o padrão quando vazio ou inválido. */
export function apiKeyHeader(input: string | null | undefined): string {
  const value = (input ?? '').trim();
  return HEADER_NAME_RE.test(value) ? value : DEFAULT_API_KEY_HEADER;
}

/**
 * A URL digitada, sem espaços, se for http(s) válida; senão `null`. A api decide o resto
 * (HTTPS obrigatório, guarda de SSRF) e responde `E_MCP_SERVER_URL_REJECTED` — aqui é só
 * para não mandar lixo.
 */
export function normalizeServerUrl(input: string): string | null {
  const value = input.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    // Devolve o texto, não `url.toString()`: este acrescenta `/` em host puro, e a api
    // reaproveita template comparando a `server_url`.
    return value;
  } catch {
    // URL que não parseia é resposta do formulário, não falha.
    return null;
  }
}

/** Modos em que quem instala informa um token. */
export function needsToken(mode: McpAuthMode | null | undefined): boolean {
  return mode === 'bearer' || mode === 'apikey';
}

/**
 * As credenciais no formato dos secrets da instância: `api_token` (bearer), `api_key`
 * (apikey). Sem token, ou modo sem token, nada vai — na edição, vazio = manter o atual.
 */
export function mcpCredentials(
  mode: McpAuthMode | null | undefined,
  token: string,
): Record<string, string> | undefined {
  const value = token.trim();
  if (!value) return undefined;
  if (mode === 'bearer') return { api_token: value };
  if (mode === 'apikey') return { api_key: value };
  return undefined;
}

/**
 * Title sugerido para a instalação: o title do servidor, o nome dele, o label do template
 * que já existe no catálogo, ou o host da URL.
 */
export function suggestedMcpTitle(inspection: McpInspectResult | null | undefined): string {
  if (!inspection) return '';
  const candidates = [
    inspection.server_info?.title,
    inspection.existing_template?.label,
    inspection.server_info?.name,
  ];
  const found = candidates.find((value) => typeof value === 'string' && value.trim());
  if (found) return found.trim();
  try {
    return new URL(inspection.server_url).hostname;
  } catch {
    // server_url vem da api; se não parsear, não há sugestão melhor do que nenhuma.
    return '';
  }
}

/**
 * Já dá para seguir para a etapa do title? Sem auth ou OAuth, basta o servidor ter
 * respondido; com token, o token precisa estar preenchido.
 */
export function canContinueAfterInspect(
  inspection: McpInspectResult | null | undefined,
  token: string,
): boolean {
  if (!inspection) return false;
  if (needsToken(inspection.auth.mode) && inspection.auth.required) return token.trim().length > 0;
  return true;
}
